import { FormEventHandler, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Input,
  Text,
  Textarea,
  chakra,
} from "@chakra-ui/react";
import isEmail from "validator/lib/isEmail";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../../lib/apiReq";
import { Icon } from "../../../components/Icon/Icon";

interface Errors {
  email?: string;
  message?: string;
  default?: string;
}

export function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [request, requestState] = useReq(apiReq);

  const contact: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const nextErrors: Errors = {};
    if (!email || !isEmail(email)) {
      nextErrors.email = "Please double check the email";
    }
    if (!message) {
      nextErrors.message = "What message would you like to send?";
    }
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) {
      requestState.clearError();
      const result = await request.post("/contact", { name, email, message });
      if (!result) {
        setErrors({
          default: "Something unexpected went wrong. Please try again.",
        });
      }
    }
  };

  return (
    <Flex direction="column" minH="100%" w="100%" bg="bg.canvas">
      <Box w="100%" maxW="40rem" mx="auto" px="4" pt="8" pb="10">
        <Heading as="h1" textStyle="hero" color="fg.default" mb="2">
          Contact
        </Heading>

        {requestState.result ? (
          <Box
            bg="bg.canvas"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="16px"
            boxShadow="card"
            p="6"
          >
            <Heading as="h2" textStyle="cardTitle" color="fg.default" mb="2">
              We have received your message!
            </Heading>
            <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
              Thanks for getting in touch. We&apos;ll get back to you via the
              email you provided in the next 48 hours.
            </Text>
          </Box>
        ) : (
          <chakra.form
            onSubmit={contact}
            bg="bg.canvas"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="16px"
            boxShadow="card"
            p="6"
            display="flex"
            flexDirection="column"
            gap="4"
          >
            <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
              Whether you&apos;ve got a question or want to chat with the
              founders, we&apos;re excited to hear from you!
            </Text>

            {errors.default && (
              <HStack
                gap="2"
                p="10px 12px"
                bg="#FEF5F5"
                borderWidth="1px"
                borderColor="fg.danger"
                borderRadius="xs"
              >
                <Icon name="x" boxSize="14px" color="fg.danger" />
                <Text fontSize="sm" color="fg.danger">
                  {errors.default}
                </Text>
              </HStack>
            )}

            <Field>
              <Input
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                h="48px"
                bg="bg.canvas"
                borderColor="border.subtle"
                borderRadius="12px"
                px="4"
                _focusVisible={{
                  borderColor: "border.brand",
                  boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
                  outline: "none",
                }}
              />
            </Field>

            <Field error={errors.email}>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                h="48px"
                bg="bg.canvas"
                borderColor={errors.email ? "fg.danger" : "border.subtle"}
                borderRadius="12px"
                px="4"
                _focusVisible={{
                  borderColor: errors.email ? "fg.danger" : "border.brand",
                  boxShadow: errors.email
                    ? "0 0 0 3px rgba(230,77,77,0.2)"
                    : "0 0 0 3px rgba(57,184,125,0.25)",
                  outline: "none",
                }}
              />
            </Field>

            <Field error={errors.message}>
              <Textarea
                placeholder="Message"
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                minH="8rem"
                bg="bg.canvas"
                borderColor={errors.message ? "fg.danger" : "border.subtle"}
                borderRadius="12px"
                p="3"
                _focusVisible={{
                  borderColor: errors.message ? "fg.danger" : "border.brand",
                  boxShadow: errors.message
                    ? "0 0 0 3px rgba(230,77,77,0.2)"
                    : "0 0 0 3px rgba(57,184,125,0.25)",
                  outline: "none",
                }}
              />
            </Field>

            <Button
              type="submit"
              h="48px"
              borderRadius="full"
              bg="bg.brand"
              color="fg.onBrand"
              fontWeight="bold"
              fontSize="md"
              _hover={{ filter: "brightness(0.95)" }}
              _active={{ filter: "brightness(0.9)" }}
              loading={requestState.loading}
              loadingText="Sending…"
            >
              Submit
            </Button>
          </chakra.form>
        )}
      </Box>
    </Flex>
  );
}

function Field({
  error,
  children,
}: {
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      {children}
      {error && (
        <chakra.div mt="1.5" fontSize="xs" color="fg.danger">
          {error}
        </chakra.div>
      )}
    </Box>
  );
}
